// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {addToast} from 'common/components/toast-area/toast-manager';
import {TranslationKey} from 'common/i18n';
import {IronContextRegistry} from 'common/report/context-registry';
import {errorReportingUtils, reporter} from 'common/report/error-reporting';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ToastType} from 'common/components/toast/types';
import {HelpTrackingEvent} from 'common/widgets/chrome/chrome-header/constants';
import {BillingAccount} from 'common/report/queries/app-api/support-info-client';

import {zendeskApi} from './api';
import {
  RequestSubtype,
  RequestType,
  Severity,
  SupportPlan,
  SupportPlanTier,
  TicketType,
  ZendeskTicketData,
  ZendeskUpload,
} from './types';
import {getSpecialFeaturesForProject, getSupportPlanTier} from './utils';
import {template} from './template';
import css from './index.inline.styl';

// 5 is the max file upload limit in the Zendesk Web Widget. Save 1 slot for console logs
const MAX_FILE_UPLOADS = 4;

export const LOCAL_STORAGE_KEY = `support_form_draft`;
const enum RequestTypeLabel {
  HelpUsingMixpanel = `help.support.request-type-product`,
  HelpWithMyAccount = `help.support.request-type-account`,
  ReportAnIssue = `help.support.request-type-issue`,
}

type RequestTypeItem = {
  fieldName: TranslationKey;
  tag: RequestType;
  options: {
    [k in TranslationKey]?: RequestSubtype;
  };
};
/**
 * The tags here should exactly match the tags defined in
 * the following fields at https://mixpanelsupport.zendesk.com
 *
 * Request Type (Field ID: 360021982112)
 * Request Subtype (Field ID: 360022979492)
 */
const REQUEST_TYPES: {[key in RequestTypeLabel]: RequestTypeItem} = {
  [RequestTypeLabel.HelpUsingMixpanel]: {
    fieldName: `help.support.product-help`,
    tag: RequestType.HelpUsingMixpanel,
    options: {
      'help.support.product-boards': RequestSubtype.DashboardHelp,
      'help.support.product-analysis': RequestSubtype.AnalysisHelp,
      'help.support.product-users': RequestSubtype.UsersHelp,
      'help.support.product-data-management': RequestSubtype.DataManagementHelp,
      'help.support.product-integrations': RequestSubtype.IntegrationsHelp,
      'help.support.product-pipelines': RequestSubtype.PipelinesHelp,
      'help.support.product-implementation': RequestSubtype.ImplementationHelp,
      'help.support.product-other': RequestSubtype.OtherHelp,
    },
  },

  [RequestTypeLabel.HelpWithMyAccount]: {
    fieldName: `help.support.account-help`,
    tag: RequestType.HelpWithMyAccount,
    options: {
      'help.support.account-billing': RequestSubtype.BillingPaymentsAndPricing,
      'help.support.account-management': RequestSubtype.AccountManagement,
      'help.support.account-data': RequestSubtype.PersonalDataAndGdpr,
      'help.support.account-other': RequestSubtype.OtherHelp,
    },
  },

  [RequestTypeLabel.ReportAnIssue]: {
    fieldName: `help.support.issue-help`,
    tag: RequestType.ReportAnIssue,
    options: {
      'help.support.issue-bug': RequestSubtype.Bug,
      'help.support.issue-reports': RequestSubtype.ReportsNotLoading,
      'help.support.issue-messages': RequestSubtype.MessagesNotSending,
      'help.support.issue-ingestion': RequestSubtype.DataIngestionIssues,
      'help.support.issue-other': RequestSubtype.OtherHelp,
    },
  },
};

interface ElementRefs {
  fileInput?: HTMLInputElement;
}

export interface SupportWidgetFormState {
  billingAccount: BillingAccount;
  comment: string;
  isDraggingAndDropping: boolean;
  isMaximized: boolean;
  isMinimized: boolean;
  isTicketSubmitting: boolean;
  isTicketSubmitted: boolean;
  isUploadingFiles: boolean;
  requestSubtypeLabel: TranslationKey;
  requestTypeLabel: RequestTypeLabel;
  subject: string;
  uploads: ZendeskUpload[];
  severity: Severity;
}

export interface SupportWidgetFormAttrs {
  'billing-account': BillingAccount;
  'support-plan': SupportPlan;
  'data-state': any;
  'events-plan-id': number;
  'feature-gate-name': string;
  'people-plan-id': number;
  'total-arr': number;
  'user-age-in-days': number;
  'user-email': string;
  'user-id': number;
  'user-name': string;
  'should-override-free-warning': boolean;
}

export class SupportWidgetForm extends Component<
  SupportWidgetFormState,
  SupportWidgetFormAttrs,
  any,
  any,
  IronContextRegistry
> {
  private elRefs: ElementRefs = {};

  static get attrsSchema(): AttrsSchema<SupportWidgetFormAttrs> {
    return {
      'billing-account': `json`,
      'support-plan': `string`,
      'data-state': `json`,
      'events-plan-id': `number`,
      'feature-gate-name': `string`,
      'people-plan-id': `number`,
      'total-arr': `number`,
      'user-age-in-days': `number`,
      'user-email': `string`,
      'user-id': `number`,
      'user-name': `string`,
      'should-override-free-warning': `boolean`,
    };
  }

  get config(): ConfigOptions<SupportWidgetFormState, any, IronContextRegistry> {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`currentProject` as const, `i18n` as const],
      defaultState: {
        billingAccount: null,
        comment: null,
        isDraggingAndDropping: false,
        isMaximized: false,
        isMinimized: false,
        isTicketSubmitting: false,
        isTicketSubmitted: false,
        isUploadingFiles: false,
        requestSubtypeLabel: null,
        requestTypeLabel: null,
        subject: null,
        uploads: [],
        severity: Severity.DEFAULT,
      },
      helpers: {
        MAX_FILE_UPLOADS,

        closeSupportWidget: () => {
          this.dispatchEvent(new CustomEvent(`close`));
          window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        },

        fileInputDragleave: () => {
          this.update({isDraggingAndDropping: false});
        },

        fileInputDragover: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          ev.dataTransfer.dropEffect = `copy`;
          this.update({isDraggingAndDropping: true});
        },

        getRequestSubtypeOptions: () => {
          const t = this.getContext(`i18n`).t;
          return Object.keys(REQUEST_TYPES[this.state.requestTypeLabel].options).map((key: TranslationKey) => ({
            label: t(key),
            value: key,
          }));
        },

        getRequestSubtypeTicketField: () => {
          return REQUEST_TYPES[this.state.requestTypeLabel].fieldName;
        },

        getRequestTypeOptions: () => {
          const t = this.getContext(`i18n`).t;
          return Object.keys(REQUEST_TYPES).map((key: RequestTypeLabel) => ({
            label: t(key),
            value: key,
          }));
        },

        handleInputComment: (ev) => {
          this.update({comment: ev.target.value});
        },

        handlePasteFromClipboard: (ev) => {
          if (ev.clipboardData.files.length > 0) {
            ev.preventDefault();
            this.helpers.uploadFilesToZendesk(ev.clipboardData.files);
          }
        },

        inputSubject: (ev) => {
          this.update({subject: ev.target.value});
        },

        isSubmitDisabled: () => {
          const {comment, isTicketSubmitting, requestSubtypeLabel, requestTypeLabel, subject} = this.state;
          const validForm = subject && comment && requestTypeLabel && requestSubtypeLabel;
          return !validForm || isTicketSubmitting;
        },

        maximizeWidget: () => {
          this.update({
            isMaximized: true,
            isMinimized: false,
          });
          this.dispatchEvent(new CustomEvent(`maximize`));
        },

        removeUploadedFile: (index) => {
          const uploads = [...this.state.uploads];
          uploads.splice(index, 1);
          this.update({uploads});
        },

        saveDraft: () => {
          const {subject, comment, uploads, requestTypeLabel, requestSubtypeLabel} = this.state;
          if (subject || comment || uploads.length > 0) {
            window.localStorage.setItem(
              LOCAL_STORAGE_KEY,
              JSON.stringify({
                subject,
                comment,
                uploads,
                requestTypeLabel,
                requestSubtypeLabel,
              }),
            );
          }
        },

        selectRequestSubtype: (ev) => {
          this.update({requestSubtypeLabel: ev.detail.item.value});
        },

        selectRequestType: (ev) => {
          this.update({
            requestSubtypeLabel: null,
            requestTypeLabel: ev.detail.item.value,
          });
        },

        selectSeverity: (ev) => {
          this.update({severity: ev.detail.item.value});
        },

        setElRef: (ref: string, el: HTMLElement) => {
          this.elRefs[ref] = el;
        },

        showSeverityDropdown: () => {
          return this.attr(`support-plan`) === SupportPlan.McdonaldsCustom;
        },

        showFreePlanWarning: () => {
          const {billingAccount, requestTypeLabel} = this.state;
          return (
            requestTypeLabel === RequestTypeLabel.HelpUsingMixpanel &&
            billingAccount &&
            getSupportPlanTier(billingAccount.arr) === SupportPlanTier.Free &&
            !this.attr(`should-override-free-warning`)
          );
        },

        submitZendeskTicket: async () => {
          this.update({isTicketSubmitting: true});
          const ticketType: TicketType = TicketType.SupportTicket;
          await this.attachLogFile();

          const {billingAccount, comment, requestSubtypeLabel, requestTypeLabel, subject, uploads, severity} =
            this.state;

          const zendeskTicketData: ZendeskTicketData = {
            eventsPlanId: this.attr(`events-plan-id`),
            features: getSpecialFeaturesForProject(this.getContext(`currentProject`).get().value),
            featureGateName: this.attr(`feature-gate-name`),
            peoplePlanId: this.attr(`people-plan-id`),
            requestSubtype: requestTypeLabel ? REQUEST_TYPES[requestTypeLabel].options[requestSubtypeLabel] : null,
            requestType: requestTypeLabel ? REQUEST_TYPES[requestTypeLabel].tag : null,
            userAgeInDays: this.attr(`user-age-in-days`),
            userEmail: this.attr(`user-email`),
            userId: this.attr(`user-id`)?.toString(),
            userName: this.attr(`user-name`) || this.attr(`user-email`),
            id: billingAccount.id,
            serviceArr: billingAccount.recurringServiceArr,
            supportPlan: this.attr(`support-plan`),
            supportPlanTier: getSupportPlanTier(billingAccount.arr),
            totalArr: billingAccount.arr,
            comment,
            subject,
            uploads,
            severity,
          };

          await zendeskApi.submitZendeskTicket(zendeskTicketData, ticketType);

          this.update({
            comment: null,
            isTicketSubmitted: true,
            isTicketSubmitting: false,
            subject: null,
            uploads: [],
          });
          window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        },

        trackHelpDocs: (ev) => {
          ev.preventDefault();
          mixpanel.track(HelpTrackingEvent.DocsLink);
          window.open(`https://docs.mixpanel.com/`, `_blank`);
        },

        toggleFullscreen: (ev) => {
          // prevent minimized header click handler from invoking
          ev.stopPropagation();
          if (this.state.isMaximized) {
            this.dispatchEvent(new CustomEvent(`dock`));
          } else {
            this.helpers.maximizeWidget();
          }
        },

        toggleMinimize: (ev, minimize: boolean) => {
          ev.stopPropagation();
          if (!minimize) {
            this.dispatchEvent(new CustomEvent(`dock`));
          }
          this.update({isMinimized: minimize});
        },

        uploadDroppedFiles: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          this.update({isDraggingAndDropping: false});
          this.helpers.uploadFilesToZendesk(ev.dataTransfer.files);
        },

        uploadFilesToZendesk: async (files: FileList) => {
          this.update({isUploadingFiles: true});
          const numUploads = Math.min(files.length, MAX_FILE_UPLOADS - this.state.uploads.length);
          for (let i = 0; i < numUploads; i++) {
            const upload = await zendeskApi.uploadZendeskFile(files[i]);
            const uploads: ZendeskUpload[] = [...this.state.uploads, upload];
            this.update({uploads});
          }
          this.update({isUploadingFiles: false});
        },

        uploadSelectedFiles: () => {
          this.helpers.uploadFilesToZendesk(this.elRefs.fileInput.files);
        },
      },

      hooks: {
        postUpdate: () => {
          this.dispatchEvent(new CustomEvent(`state-change`, {detail: this.state}));
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
    const supportFormDraft = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (supportFormDraft) {
      this.update(JSON.parse(supportFormDraft));
    }
    window.addEventListener(`beforeunload`, this.helpers.saveDraft);
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    // This ensures the full-page /get-support modal that loads right away will
    // work, since `billing-account` is fetched from an async request and
    // state.billingAccount would stay null if set in connectedCallback()
    if (attr === `billing-account`) {
      const billingAccount = this.attr(`billing-account`);
      this.update({billingAccount});
    }
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(`beforeunload`, this.helpers.saveDraft);
  }

  private async attachLogFile() {
    const consoleLogs = errorReportingUtils.getConsoleLogs();
    // skip attaching the file if there are no console errors
    if (!consoleLogs.error.length) {
      return;
    }

    const errors = errorReportingUtils
      .getConsoleLogs()
      .error.map((msg) => [msg.message, msg.stack, msg.rollbarLink].filter(Boolean).join(`\n`));
    const logBody = `Console Errors:\n\n${errors.join(`\n\n`)}`;
    const fileName = `logs_${this.attr(`user-email`).replace(/\./g, `_`)}.txt`;
    const logFile = new File([logBody], fileName, {type: `text/plain`});

    try {
      const upload = await zendeskApi.uploadZendeskFile(logFile);
      this.update({uploads: [...this.state.uploads, upload]});
    } catch (error) {
      reporter.error(error);
      addToast({message: `Error attaching logs, your ticket will be sent without them`, type: ToastType.ERROR});
    }
  }
}

export const SupportWidgetFormEl = defineAndMakeJsxFactory(`support-widget-form`, SupportWidgetForm);
export const SupportWidgetFormReact = panel2react(`support-widget-form`, SupportWidgetForm);
